import { useState } from "react";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  TAXONOMY_GAP_ENTRY_TYPES,
  TAXONOMY_JOURNAL_TIERS,
  TAXONOMY_PARTY_TYPES,
  type TaxonomyGapEntry,
} from "@shared/types/taxonomy.ts";
import { SectionKey } from "@shared/types/section_key.ts";
import type { TaxonomyGapReview } from "@shared/types/corpus/api.ts";
import { FieldPicker } from "../../../../components/domain/inputs/FieldPicker";
import { DocumentLink } from "../../../../components/domain/data-display/DocumentLink";
import { FormDialog } from "../../../../components/primitives/feedback/FormDialog/FormDialog";
import { useFormDialog } from "../../../../components/primitives/feedback/FormDialog/useFormDialog";
import {
  readGapEntryDraft,
  startGapEntryDraft,
  type GapEntryDraft,
  type SectionChoice,
} from "./gapEntryDraft";

const ENTRY_LABELS: Record<TaxonomyGapEntry["entry"], string> = {
  journal: "Journal",
  party: "A funder",
  non_party: "Not a funder",
  section_title: "Section title",
  mesh_prefix: "MeSH prefix",
};

interface DecideGapDialogProps {
  gap: TaxonomyGapReview;
  onClose: () => void;
  /** Called with the entry when the person saves. Throw to keep the dialog open with the error. */
  onDecide: (entry: TaxonomyGapEntry) => Promise<void>;
}

/** What the judge or the documents say about a gap, then the form for the entry that settles it. */
export function DecideGapDialog({ gap, onClose, onDecide }: DecideGapDialogProps) {
  const [draft, setDraft] = useState<GapEntryDraft>(() => startGapEntryDraft(gap));
  const reading = readGapEntryDraft(draft);
  const update = (change: Partial<GapEntryDraft>) =>
    setDraft((current) => ({ ...current, ...change }));
  const entryTypes = TAXONOMY_GAP_ENTRY_TYPES[gap.kind];

  const { dialogProps } = useFormDialog({
    onSubmit: async () => {
      if (reading.entry === null) throw new Error(reading.problem ?? "The entry is incomplete.");
      await onDecide(reading.entry);
    },
    onClose,
  });

  return (
    <FormDialog
      open
      title={`Decide “${gap.value}”`}
      submitLabel="Save decision"
      submitDisabled={reading.entry === null}
      {...dialogProps}
    >
      {gap.escalationReason && (
        <Typography variant="body2" color="text.secondary">
          The judge could not settle it: {gap.escalationReason}
        </Typography>
      )}
      {gap.context?.label && (
        <Typography variant="body2" color="text.secondary">
          {gap.context.label}
          {gap.context.tree_numbers.length > 0 && ` · ${gap.context.tree_numbers.join(", ")}`}
        </Typography>
      )}
      <Stack gap={0.5}>
        <Typography variant="caption" color="text.secondary">
          Hit by {gap.documentsTotal} document{gap.documentsTotal === 1 ? "" : "s"}
        </Typography>
        {gap.documents.map((document) => (
          <DocumentLink key={document.sourceId} title={document.url} url={document.url} />
        ))}
      </Stack>

      {entryTypes.length > 1 && (
        <TextField
          select
          size="small"
          label="This is"
          value={draft.entryType}
          onChange={(e) => update({ entryType: e.target.value as GapEntryDraft["entryType"] })}
        >
          {entryTypes.map((entryType) => (
            <MenuItem key={entryType} value={entryType}>
              {ENTRY_LABELS[entryType]}
            </MenuItem>
          ))}
        </TextField>
      )}

      {draft.entryType === "journal" && (
        <>
          <TextField
            select
            size="small"
            label="Tier"
            value={draft.tier}
            onChange={(e) => update({ tier: e.target.value as GapEntryDraft["tier"] })}
          >
            <MenuItem value="">None</MenuItem>
            {TAXONOMY_JOURNAL_TIERS.map((tier) => (
              <MenuItem key={tier} value={tier}>
                {tier.replace("_", " ")}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Indexed by MEDLINE"
            value={draft.medlineIndexed}
            onChange={(e) =>
              update({ medlineIndexed: e.target.value as GapEntryDraft["medlineIndexed"] })
            }
          >
            <MenuItem value="">Not asked</MenuItem>
            <MenuItem value="yes">Yes</MenuItem>
            <MenuItem value="no">No</MenuItem>
          </TextField>
        </>
      )}

      {draft.entryType === "party" && (
        <>
          <TextField
            select
            size="small"
            label="Type"
            value={draft.partyType}
            onChange={(e) => update({ partyType: e.target.value as GapEntryDraft["partyType"] })}
          >
            {TAXONOMY_PARTY_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Controlled by (a known company, optional)"
            value={draft.controlledBy}
            onChange={(e) => update({ controlledBy: e.target.value })}
          />
        </>
      )}

      {draft.entryType === "section_title" && (
        <TextField
          select
          size="small"
          label="Section"
          value={draft.sectionChoice}
          onChange={(e) => update({ sectionChoice: e.target.value as SectionChoice })}
        >
          {Object.values(SectionKey).map((key) => (
            <MenuItem key={key} value={key}>
              {key}
            </MenuItem>
          ))}
          <MenuItem value="noise">Noise (not a section)</MenuItem>
        </TextField>
      )}

      {draft.entryType === "mesh_prefix" && (
        <>
          <TextField
            size="small"
            label="Tree prefix"
            placeholder="C14.280"
            value={draft.prefix}
            onChange={(e) => update({ prefix: e.target.value })}
          />
          <FieldPicker size="small" value={draft.urns} onChange={(urns) => update({ urns })} />
          <FormControlLabel
            label="The fields ignore this prefix instead of mapping to it"
            control={
              <Switch
                checked={draft.ignored}
                onChange={(e) => update({ ignored: e.target.checked })}
              />
            }
          />
        </>
      )}

      {reading.problem && (
        <Typography variant="caption" color="text.secondary">
          {reading.problem}
        </Typography>
      )}
    </FormDialog>
  );
}
